/**
 * Platform operatör konsolu sözleşmesi (Castinflow v3.1 Dalga 1 B1/B2; ADR-0015 § 1–4, § 8).
 *
 * - Operatör kiracı kullanıcısı DEĞİLDİR: ayrı kimlik tablosu (`platform.operators`), ayrı köken, ayrı oturum çerezi ve zorunlu
 *   MFA. Kiracı belirteci konsolda, operatör oturumu kiracı API'sinde geçersizdir.
 * - Yetki matrisi (ADR-0015 § 2) TEK kaynaktır: `OPERATOR_CAPABILITY_ROLES`. Sunucu her uçta buradan karar verir; veritabanı
 *   fonksiyonları aynı rol kümelerini ayrıca denetler. Arayüz bu sözleşmeyi yalnız gösterim için okur.
 * - Envanter, ayrıntı ve işletim yanıtları KAYIT İÇERİĞİ taşımaz; şemalar `.strict()` — fazladan alan yanıtı geçersiz kılar
 *   (sunucu yanıtı bu şemayla doğrular, test kilitler).
 */
import { z } from 'zod';
import { planIdSchema } from './plans.js';
import { TENANT_STATUSES } from './data-lifecycle.js';
// ---------------------------------------------------------------------------------------------------------------------
// Roller ve yetki matrisi
// ---------------------------------------------------------------------------------------------------------------------
/** Sıra = en az ayrıcalıklıdan en geniş olana; denetim satırına işlemi yetkilendiren İLK rol yazılır. */
export const OPERATOR_ROLES = ['destek', 'satis', 'platform'];
export const operatorRoleSchema = z.enum(OPERATOR_ROLES);
export const OPERATOR_ROLE_LABELS = {
    destek: 'Destek',
    satis: 'Satış',
    platform: 'Platform',
};
/**
 * ADR-0015 § 2 tablosu. `audit.read` (operatör denetim kaydını görüntüleme) tabloya bu dilimde eklendi: yalnız `platform`.
 * Bu dilimde uygulanan uçlar: `tenants.read`, `operations.read`, `operators.manage`, `audit.read`. Diğerleri sonraki
 * dilimlerin (B3–B5) uçları içindir; matris şimdiden sabittir (test).
 */
export const OPERATOR_CAPABILITY_ROLES = {
    'tenants.read': ['destek', 'satis', 'platform'],
    'operations.read': ['destek', 'platform'],
    'support.use': ['destek', 'platform'],
    'tenants.provision': ['satis', 'platform'],
    'trial.extend': ['satis', 'platform'],
    'commercial_notes.write': ['satis', 'platform'],
    'plan.assign': ['platform'],
    'tenants.suspend': ['platform'],
    'effects.release': ['platform'],
    'operators.manage': ['platform'],
    'audit.read': ['platform'],
};
export const OPERATOR_CAPABILITIES = Object.keys(OPERATOR_CAPABILITY_ROLES);
export const operatorCapabilitySchema = z.enum(OPERATOR_CAPABILITIES);
/** Hassas işlemler: son `STEP_UP_WINDOW_SECONDS` içinde yeniden doğrulama (parola + MFA kodu) ister (ADR-0015 § 1.3). */
export const STEP_UP_CAPABILITIES = [
    'plan.assign',
    'tenants.suspend',
    'operators.manage',
    'trial.extend',
];
export const STEP_UP_WINDOW_SECONDS = 600;
/** Oturum: boşta 30 dk, en fazla 8 saat; parola sonrası (MFA bekleyen) ara oturum en fazla 10 dk. */
export const OPS_SESSION_IDLE_SECONDS = 1800;
export const OPS_SESSION_MAX_SECONDS = 8 * 3600;
export const OPS_PENDING_SESSION_SECONDS = 600;
export function rolesAllowedFor(cap) {
    return OPERATOR_CAPABILITY_ROLES[cap];
}
/** İşlemi yetkilendiren rol: operatörün taşıdığı ve izinli olan İLK rol (en az ayrıcalıklı); yoksa `null`. */
export function authorizingRole(roles, cap) {
    const allowed = OPERATOR_CAPABILITY_ROLES[cap];
    return OPERATOR_ROLES.find((r) => roles.includes(r) && allowed.includes(r)) ?? null;
}
export function operatorCapabilities(roles) {
    return OPERATOR_CAPABILITIES.filter((c) => authorizingRole(roles, c) !== null);
}
// ---------------------------------------------------------------------------------------------------------------------
// Kimlik ve oturum
// ---------------------------------------------------------------------------------------------------------------------
export const OPERATOR_PASSWORD_MIN = 12;
export const OPERATOR_PASSWORD_MAX = 128;
export const operatorEmailSchema = z.string().trim().toLowerCase().check(z.email()).max(254);
export const operatorPasswordSchema = z
    .string()
    .min(OPERATOR_PASSWORD_MIN, `Parola en az ${OPERATOR_PASSWORD_MIN} karakter olmalı`)
    .max(OPERATOR_PASSWORD_MAX)
    .refine((v) => v.trim() === v, 'Parola boşlukla başlayıp bitemez');
export const operatorRolesSchema = z
    .array(operatorRoleSchema)
    .min(1, 'En az bir rol seçin')
    .max(3)
    .refine((v) => new Set(v).size === v.length, 'Rol yinelenemez')
    .transform((v) => OPERATOR_ROLES.filter((r) => v.includes(r)));
export const operatorDisplayNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .refine((v) => !/\p{Cc}/u.test(v), 'Ad denetim karakteri içeremez');
export const opsTotpCodeSchema = z.string().regex(/^\d{6}$/, 'Doğrulama kodu 6 haneli olmalı');
export const opsLoginInputSchema = z
    .object({ email: operatorEmailSchema, password: z.string().min(1).max(OPERATOR_PASSWORD_MAX) })
    .strict();
export const opsMfaCodeInputSchema = z.object({ code: opsTotpCodeSchema }).strict();
export const opsStepUpInputSchema = z
    .object({ password: z.string().min(1).max(OPERATOR_PASSWORD_MAX), code: opsTotpCodeSchema })
    .strict();
export const OPS_SESSION_STAGES = ['mfa', 'enroll', 'active'];
/** Parola adımından sonra: `mfa` = kayıtlı ikinci etkenle doğrula · `enroll` = MFA kaydı zorunlu (ilk giriş) */
export const opsLoginResponseSchema = z.object({ stage: z.enum(['mfa', 'enroll']) }).strict();
const isoDateTime = z.iso.datetime({ offset: true });
const uuid = z.guid();
export const opsMeSchema = z
    .object({
    stage: z.enum(OPS_SESSION_STAGES),
    operator: z
        .object({
        id: uuid,
        email: z.string(),
        displayName: z.string(),
        roles: z.array(operatorRoleSchema),
    })
        .strict(),
    /** Ara oturumda (stage ≠ active) boştur */
    capabilities: z.array(operatorCapabilitySchema),
    session: z
        .object({
        expiresAt: isoDateTime,
        idleTimeoutSeconds: z.number().int(),
        /** Adım yükseltme geçerlilik sonu (yoksa null) */
        stepUpValidUntil: isoDateTime.nullable(),
    })
        .strict(),
})
    .strict();
export const opsTotpEnrollStartSchema = z
    .object({ secret: z.string(), otpauthUrl: z.string() })
    .strict();
export const opsStepUpResponseSchema = z.object({ stepUpValidUntil: isoDateTime }).strict();
/** Hata ayrıntısındaki `reason` değerleri (arayüz yönlendirmesi) */
export const OPS_ERROR_REASONS = [
    'mfa_required',
    'mfa_enrollment_required',
    'step_up_required',
    'self_target',
    'operator_forbidden',
    'session_idle',
    'session_expired',
    'email_taken',
];
// ---------------------------------------------------------------------------------------------------------------------
// Operatör yönetimi
// ---------------------------------------------------------------------------------------------------------------------
export const OPERATOR_STATUSES = ['active', 'disabled'];
export const operatorCreateInputSchema = z
    .object({
    email: operatorEmailSchema,
    displayName: operatorDisplayNameSchema,
    roles: operatorRolesSchema,
    password: operatorPasswordSchema,
})
    .strict();
export const operatorUpdateInputSchema = z
    .object({
    displayName: operatorDisplayNameSchema.optional(),
    roles: operatorRolesSchema.optional(),
    status: z.enum(OPERATOR_STATUSES).optional(),
})
    .strict()
    .refine((v) => v.displayName !== undefined || v.roles !== undefined || v.status !== undefined, 'En az bir alan değişmeli');
export const operatorListItemSchema = z
    .object({
    id: uuid,
    email: z.string(),
    displayName: z.string(),
    roles: z.array(operatorRoleSchema),
    status: z.enum(OPERATOR_STATUSES),
    mfaEnrolled: z.boolean(),
    lastLoginAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
})
    .strict();
export const operatorListResponseSchema = z
    .object({ data: z.array(operatorListItemSchema) })
    .strict();
// ---------------------------------------------------------------------------------------------------------------------
// Kiracı envanteri, ayrıntı, işletim görünümü (içerik YOK)
// ---------------------------------------------------------------------------------------------------------------------
/** exempt: deneme kuralı dışı · none: deneme dışı plan · legacy: süresi tanımsız eski deneme · expired · active */
export const TENANT_TRIAL_STATES = ['legacy', 'active', 'expired', 'exempt', 'none'];
export const TENANT_TRIAL_FILTERS = ['any', 'expiring', ...TENANT_TRIAL_STATES];
export const TENANT_LAST_LOGIN_FILTERS = [
    'any',
    'within_7d',
    'within_30d',
    'older_30d',
    'never',
];
export const TENANT_INVENTORY_SORTS = [
    'name',
    'created_desc',
    'last_login_desc',
    'trial_end_asc',
];
export const TENANT_INVENTORY_MAX_LIMIT = 200;
export const tenantInventoryQuerySchema = z
    .object({
    q: z.string().trim().max(100).optional(),
    status: z.enum(TENANT_STATUSES).optional(),
    plan: planIdSchema.optional(),
    trial: z.enum(TENANT_TRIAL_FILTERS).default('any'),
    quotaPressure: z.boolean().default(false),
    lastLogin: z.enum(TENANT_LAST_LOGIN_FILTERS).default('any'),
    sort: z.enum(TENANT_INVENTORY_SORTS).default('name'),
    limit: z.number().int().min(1).max(TENANT_INVENTORY_MAX_LIMIT).default(50),
    offset: z.number().int().min(0).max(100_000).default(0),
})
    .strict();
export const tenantInventoryRowSchema = z
    .object({
    id: uuid,
    slug: z.string(),
    name: z.string(),
    status: z.enum(TENANT_STATUSES),
    planId: z.string(),
    planName: z.string(),
    isDemo: z.boolean(),
    createdAt: isoDateTime,
    trialEndsAt: isoDateTime.nullable(),
    trialState: z.enum(TENANT_TRIAL_STATES),
    lifecycleExempt: z.boolean(),
    userCount: z.number().int(),
    lastLoginAt: isoDateTime.nullable(),
    /** Kaynaklar içinde en yüksek doluluk yüzdesi (tavanı tanımlı olanlar); ölçüm yoksa null */
    quotaPeakPercent: z.number().int().nullable(),
})
    .strict();
export const tenantInventoryResponseSchema = z
    .object({
    data: z.array(tenantInventoryRowSchema),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
})
    .strict();
export const tenantDetailSchema = z
    .object({
    tenant: z
        .object({
        id: uuid,
        slug: z.string(),
        name: z.string(),
        status: z.enum(TENANT_STATUSES),
        isDemo: z.boolean(),
        createdAt: isoDateTime,
        suspendedAt: isoDateTime.nullable(),
        closingAt: isoDateTime.nullable(),
        closedAt: isoDateTime.nullable(),
        purgeAfter: isoDateTime.nullable(),
    })
        .strict(),
    plan: z
        .object({ id: z.string(), name: z.string(), assignedAt: isoDateTime.nullable() })
        .strict(),
    trial: z
        .object({
        state: z.enum(TENANT_TRIAL_STATES),
        endsAt: isoDateTime.nullable(),
        convertedAt: isoDateTime.nullable(),
        exempt: z.boolean(),
    })
        .strict(),
    users: z
        .object({
        active: z.number().int(),
        invited: z.number().int(),
        suspended: z.number().int(),
        lastLoginAt: isoDateTime.nullable(),
    })
        .strict(),
    usage: z.array(z
        .object({
        key: z.string(),
        used: z.number(),
        /** null = sınırsız */
        limit: z.number().nullable(),
        /** Çalışma alanına özel tavan uygulanıyor mu */
        override: z.boolean(),
    })
        .strict()),
})
    .strict();
export const tenantOperationsSchema = z
    .object({
    queueDepth: z.number().int(),
    quarantined: z.number().int(),
    failedJobs24h: z.number().int(),
    unknownEffects: z.number().int(),
    webhookFailures24h: z.number().int(),
    imapAccountsFailing: z.number().int(),
    imapMessagesFailed24h: z.number().int(),
    quota: z.object({ at80: z.number().int(), at100: z.number().int() }).strict(),
})
    .strict();
export const opsPlanOptionsSchema = z
    .object({ data: z.array(z.object({ id: z.string(), name: z.string() }).strict()) })
    .strict();
// ---------------------------------------------------------------------------------------------------------------------
// Operatör denetim kaydı
// ---------------------------------------------------------------------------------------------------------------------
export const OPERATOR_AUDIT_SOURCES = ['console', 'cli', 'break_glass'];
export const OPERATOR_AUDIT_RESULTS = ['success', 'denied', 'failure'];
export const operatorAuditQuerySchema = z
    .object({
    before: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    operatorId: uuid.optional(),
    tenantId: uuid.optional(),
})
    .strict();
export const operatorAuditEntrySchema = z
    .object({
    seq: z.number().int(),
    at: isoDateTime,
    operatorId: uuid.nullable(),
    operatorEmail: z.string().nullable(),
    role: operatorRoleSchema.nullable(),
    action: z.string(),
    targetTenantId: uuid.nullable(),
    targetOperatorId: uuid.nullable(),
    reasonCode: z.string().nullable(),
    reasonText: z.string().nullable(),
    requestId: z.string().nullable(),
    source: z.enum(OPERATOR_AUDIT_SOURCES),
    result: z.enum(OPERATOR_AUDIT_RESULTS),
    actorLabel: z.string().nullable(),
    detail: z.record(z.string(), z.unknown()),
})
    .strict();
export const operatorAuditListResponseSchema = z
    .object({ data: z.array(operatorAuditEntrySchema), nextBefore: z.number().int().nullable() })
    .strict();
export const operatorAuditVerifySchema = z
    .object({
    checked: z.number().int(),
    ok: z.boolean(),
    firstBrokenSeq: z.number().int().nullable(),
    headSeq: z.number().int(),
})
    .strict();
//# sourceMappingURL=ops-console.js.map